<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref } from 'vue';
import InputNumber from 'primevue/inputnumber';
import InputGroup from 'primevue/inputgroup';
import InputGroupAddon from 'primevue/inputgroupaddon';
import { ToggleSwitch, MultiSelect, Select } from 'primevue';
import { useSelectedStorageAreaStore } from '../stores/selectedStorageAreaStore';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { ProductType } from 'shared/products/domain/productType';
import { Assert } from 'shared/assert';
import { useEditInventoryStore } from '@/storageAreas/logYards/stores/editInventoryStore';
import { useProductStore } from 'shared/products/stores/productStore';
import { useAuthStore } from '@/auth/store/authStore';
import type { Product } from 'shared/products/domain/product';
import { useSelectedBulkStore } from '../stores/selectedBulkStore';
import BulkContent from '@/storageAreas/bulk/components/BulkContent.vue';

const productStore = useProductStore();

const localStorageKey = 'bulkAllowedProductsFilterText';

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);
const selectedStorageAreaStore = useSelectedStorageAreaStore();
const selectedBulkStore = useSelectedBulkStore();
const editInventoryStore = useEditInventoryStore();
const authStore = useAuthStore();

const productOptions = computed<Product[]>(() => {

  if (allowMultipleProducts.value == false && contents.value.length > 0) {
    return productStore.sortedByName.filter(p => p.type === ProductType.Bulk && contents.value.some(c => c.productId == p.id));
  }

  const products = productStore.sortedByName.filter(p => p.type === ProductType.Bulk)
  return products;
});

const {
  automaticProductChange,
  automaticProductChangeChanged,
  automaticProductChangeEnabled,
  allowMultipleProducts,
  allowMultipleProductsChanged,
  allowMultipleProductsEnabled,
  allowedProducts,
  allowedProductsChanged,
  allowedProductsEnabled,
  contents,
  maxWeight,
  maxWeightChanged,
  maxWeightEnabled,
  maxVolume,
  maxVolumeChanged,
  maxVolumeEnabled,
  editMultiple
} = storeToRefs(selectedBulkStore);

function openContent(productId: string) {
  const storageAreaId = selectedStorageAreaStore.storageAreaId;
  Assert.definied(storageAreaId);
  editInventoryStore.show({
    productId: productId,
    storageAreaId: storageAreaId
  });
}

const filterText = ref('');

function saveFilterText(filter: string) {
  localStorage.setItem(localStorageKey, filter);
  filterText.value = filter;
}

onMounted(() => {
  const storedFilterText = localStorage.getItem(localStorageKey);
  if (storedFilterText) {
    filterText.value = storedFilterText;
  }
});

</script>

<template>

  <div v-if="allowMultipleProducts" class="field" :class="{ changed: allowedProductsChanged }">
    <label for="multiselect-storage-area-drawer-allowed-products">{{ translations.dialogs.storageArea.allowedProducts
      }}</label>
    <MultiSelect autocomplete="off" id="multiselect-storage-area-drawer-allowed-products" name="allowed-products"
      :options="productOptions" optionLabel="name" v-model="allowedProducts"
      :placeholder="translations.dialogs.storageArea.selectAllowedProducts" filter
      @filter="(e) => saveFilterText(e.value)" :pt="{ root: (root) => { root.state.filterValue = filterText } }"
      :disabled="!allowedProductsEnabled">
    </MultiSelect>
  </div>

  <div v-if="!allowMultipleProducts" class="field" :class="{ changed: allowedProductsChanged }">
    <label for="select-storage-area-drawer-allowed-products">{{ translations.dialogs.storageArea.allowedProducts
      }}</label>
    <Select ref='select' autocomplete="off" id="select-storage-area-drawer-allowed-products" name="allowed-products"
      :options="productOptions" optionLabel="name" v-model="allowedProducts[0]"
      :placeholder="translations.dialogs.storageArea.selectAllowedProducts" filter
      @filter="(e) => saveFilterText(e.value)" :pt="{ root: (root) => { root.state.filterValue = filterText } }"
      :disabled="!allowedProductsEnabled">
      <template #value="slotProps">
        <div v-if="slotProps.value" class="flex items-center">
          <div>{{ slotProps.value.name }}</div>
        </div>
        <span v-else>
          {{ slotProps.placeholder }}
        </span>
      </template>
      <template #dropdownicon>
        <i v-if="allowedProducts.length > 0" class="pi pi-times" style="font-size: 14px" @click="allowedProducts = []" />
      </template>
    </Select>
  </div>

  <div class="toggle-field" :class="{ changed: automaticProductChangeChanged }">
    <ToggleSwitch v-model:modelValue="automaticProductChange" id="storage-area-drawer-automatic-product-change"
      :disabled="!authStore.isAdmin || !automaticProductChangeEnabled">
    </ToggleSwitch>
    <label for="storage-area-drawer-automatic-product-change">{{ translations.dialogs.storageArea.automaticProductChange
    }}</label>
  </div>

  <div class="toggle-field" :class="{ changed: allowMultipleProductsChanged }">
    <ToggleSwitch v-model:modelValue="allowMultipleProducts" id="storage-area-drawer-is-allow-multiple-products"
      :disabled="!authStore.isAdmin || !allowMultipleProductsEnabled">
    </ToggleSwitch>
    <label for="storage-area-drawer-is-allow-multiple-products">{{
  translations.dialogs.storageArea.allowMultipleProducts }}</label>
  </div>

  <div class="bulk-data-grid">
    <div class="field" :class="{ changed: maxWeightChanged }">
      <label for="bulk-storage-maxWeight">{{ translations.bulk.maxWeight }}</label>
      <InputGroup>
        <InputNumber :min="0" pt:pcInputText:root:autocomplete="off" v-model:modelValue="maxWeight" id="bulk-storage-maxWeight">
        </InputNumber>
        <InputGroupAddon>t</InputGroupAddon>
      </InputGroup>
    </div>
    <div class="field" :class="{ changed: maxVolumeChanged }">
      <label for="bulk-storage-maxVolume">{{ translations.bulk.maxVolume }}</label>
      <InputGroup>
        <InputNumber :min="0" pt:pcInputText:root:autocomplete="off" v-model:modelValue="maxVolume" id="bulk-storage-maxVolume">
        </InputNumber>
        <InputGroupAddon>m³</InputGroupAddon>
      </InputGroup>
    </div>
  </div>
  <BulkContent v-if="!editMultiple" :products="contents" @click="(ev) => openContent(ev.productId)"></BulkContent>

</template>

<style scoped>
.field :deep(.p-inputnumber-input) {
  text-align: right;
}

:deep(.p-inputgroup) {
  height: 33px;
}

.bulk-data-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  row-gap: .5rem;
  column-gap: .5rem;
}

.coil-stack-view {
  grid-column: 1/3;
  overflow-x: auto;
}
</style>
